import React, { useState, useCallback, useMemo } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import { DataGrid } from '@mui/x-data-grid'
import { alpha } from '@mui/material/styles'
import EngineeringIcon from '@mui/icons-material/Engineering'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import ComputerIcon from '@mui/icons-material/Computer'
import { FC, useCQuery, invalidateQuery } from '../../Services'

const CLIENT_STATUS_MAP = {
  running: { label: 'Online', color: '#4caf50' },
  created: { label: 'Creato', color: '#2196f3' },
  pending: { label: 'In Aggiornamento', color: '#ff9800' },
  maintenance: { label: 'In Manutenzione', color: '#9c27b0' },
  offline: { label: 'Offline', color: '#f44336' }
}

const extractClients = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (Array.isArray(raw.clients)) return raw.clients
  if (Array.isArray(raw.data)) return raw.data
  return []
}

export const Maintenance = () => {
  const { data: maintenance, isLoading } = useCQuery('maintenance')
  const { data: clientsRaw = [], isLoading: isLoadingClients } = useCQuery('clients')
  const [toggling, setToggling] = useState(false)

  const clients = useMemo(() => extractClients(clientsRaw), [clientsRaw])

  const statusSummary = useMemo(() => {
    const summary = {}
    for (const key of Object.keys(CLIENT_STATUS_MAP)) {
      summary[key] = clients.filter(c => c.status === key).length
    }
    return summary
  }, [clients])

  const clientColumns = useMemo(() => [
    {
      field: 'status',
      headerName: 'Stato',
      width: 130,
      sortable: true,
      renderCell: ({ value }) => {
        const cfg = CLIENT_STATUS_MAP[value] || CLIENT_STATUS_MAP.offline
        return (
          <Stack direction='row' alignItems='center' spacing={1}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cfg.color, flexShrink: 0 }} />
            <Typography variant='body2' sx={{ fontSize: 13, fontWeight: 500 }}>
              {cfg.label}
            </Typography>
          </Stack>
        )
      }
    },
    { field: '_id', headerName: 'Client ID', flex: 1, minWidth: 180 },
    { field: 'agencyId', headerName: 'ID Agenzia', flex: 0.8, minWidth: 120 },
    { field: 'agencyName', headerName: 'Nome Agenzia', flex: 1, minWidth: 150 },
    {
      field: 'machineName',
      headerName: 'Nome macchina',
      width: 140,
      valueGetter: (value, row) => row?.additionalInfo?.machineName || '—'
    },
    {
      field: 'ip',
      headerName: 'IP',
      width: 130,
      valueGetter: (value, row) => row?.additionalInfo?.ip || '—',
      renderCell: ({ value }) => (
        <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: 13 }}>
          {value || '—'}
        </Typography>
      )
    }
  ], [])

  const handleToggle = useCallback(async () => {
    setToggling(true)
    try {
      const newActive = !maintenance?.active
      await FC.service('maintenance').patch('global', { active: newActive })
      invalidateQuery('maintenance')
      window.growl.show({
        severity: 'success',
        summary: 'Successo',
        detail: newActive
          ? 'Modalità manutenzione attivata. I client completano i file già in elaborazione e non avviano nuovi job fino alla disattivazione.'
          : 'Modalità manutenzione disattivata. I client riprenderanno il processamento.'
      })
    } catch (err) {
      window.growl.show({
        severity: 'error',
        summary: 'Errore',
        detail: err?.message || 'Impossibile modificare la modalità manutenzione'
      })
    }
    setToggling(false)
  }, [maintenance?.active])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Stack alignItems='center' spacing={2}>
          <CircularProgress size={40} />
          <Typography variant='body2' color='text.secondary'>
            Caricamento...
          </Typography>
        </Stack>
      </Box>
    )
  }

  const isActive = maintenance?.active

  return (
    <Box sx={{ mx: 'auto', width: '94%', maxWidth: 1200, pb: 5 }}>
      <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 3, mt: 1 }}>
        <EngineeringIcon color='primary' sx={{ fontSize: 28 }} />
        <Typography variant='h5' fontWeight={700} color='text.primary'>
          Modalità Manutenzione
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Stack direction='row' alignItems='center' spacing={2}>
            <FiberManualRecordIcon
              sx={{ fontSize: 14, color: isActive ? 'error.main' : 'success.main' }}
            />
            <Typography variant='h6' fontWeight={600}>
              {isActive ? 'Manutenzione attiva' : 'Sistema operativo'}
            </Typography>
            <Chip
              label={isActive ? 'ATTIVA' : 'NON ATTIVA'}
              color={isActive ? 'error' : 'success'}
              size='small'
              sx={{ fontWeight: 600 }}
            />
          </Stack>
          <Stack direction='row' alignItems='center' spacing={1}>
            {toggling && <CircularProgress size={20} />}
            <Switch
              checked={!!isActive}
              onChange={handleToggle}
              disabled={toggling}
              color='error'
            />
          </Stack>
        </Stack>

        {isActive && maintenance?.activatedAt && (
          <Alert severity='warning' sx={{ mt: 2.5 }} variant='outlined'>
            Attivata il <strong>{new Date(maintenance.activatedAt).toLocaleString('it-IT')}</strong>
            {maintenance.activatedBy ? <> da <strong>{maintenance.activatedBy}</strong></> : ''}
          </Alert>
        )}

        {!isActive && maintenance?.deactivatedAt && (
          <Alert severity='success' sx={{ mt: 2.5 }} variant='outlined'>
            Ultima disattivazione: <strong>{new Date(maintenance.deactivatedAt).toLocaleString('it-IT')}</strong>
          </Alert>
        )}

        {maintenance?.connectedClients !== undefined && (
          <Stack direction='row' alignItems='center' spacing={1} sx={{ mt: 2.5 }}>
            <Typography variant='body2' color='text.secondary'>
              Client connessi:
            </Typography>
            <Chip
              label={maintenance.connectedClients}
              size='small'
              variant='outlined'
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.8 }}>
          Quando la modalità manutenzione è <strong>attiva</strong>, tutti i client smettono di processare nuovi file.
          I file attualmente in corso di processamento vengono completati e consegnati normalmente.
          <br />
          Attiva questa modalità qualche minuto prima di un intervento di manutenzione per evitare
          interruzioni brusche. Al termine della manutenzione, disattiva lo switch per riprendere le operazioni.
        </Typography>
      </Paper>

      {/* Client Status Detail */}
      <Paper elevation={0} sx={{ mt: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <ComputerIcon fontSize='small' color='action' />
            <Typography variant='subtitle1' fontWeight={600}>
              Stato Client
            </Typography>
            <Chip label={`${clients.length} totali`} size='small' variant='outlined' sx={{ fontWeight: 500 }} />
          </Stack>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {Object.entries(CLIENT_STATUS_MAP).map(([key, { label, color }]) => (
              <Chip
                key={key}
                icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, ml: '8px !important' }} />}
                label={`${label}: ${statusSummary[key] || 0}`}
                size='small'
                variant='outlined'
                sx={{ fontWeight: 500, fontSize: 12 }}
              />
            ))}
          </Stack>
        </Box>

        <Divider />

        {isLoadingClients ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <DataGrid
            rows={clients}
            columns={clientColumns}
            getRowId={(row) => row._id}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: { sortModel: [{ field: 'status', sort: 'asc' }] }
            }}
            slots={{
              noRowsOverlay: () => (
                <Stack alignItems='center' justifyContent='center' sx={{ height: '100%', gap: 1, py: 4 }}>
                  <ComputerIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography color='text.secondary'>Nessun client registrato</Typography>
                </Stack>
              )
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
              '& .MuiDataGrid-row:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }
            }}
          />
        )}
      </Paper>
    </Box>
  )
}
